/**
 * @file   src\Pages\CWOAddTicket.tsx
 * @brief  AddTicket page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { Container, Button, Row, Col, useOutletContext, useNavigate, useEffect, useState, useRef, React } from '../utils/thirdpartyComponents';
import CWSelect from '../components/CWSelect';
import CWInput from '../components/CWInput';
import CWTextarea from '../components/CWTextarea';
import { AppContextType, COTicketData } from '../interfaces/generalInterface';
import { pageURLs } from '../utils/pageUrls';
import { SUBJECT_MAX_LENGTH, MESSAGE_MAX_LENGTH } from '../utils/constants';
import { cwoaddticketSchema } from '../validations/cwoaddticketSchema';
import * as Yup from 'yup'
import { LocationApi, TicketApi } from '../utils/apiUrls';
import { LocationItem, Option } from '../interfaces/subscriptionInterface';
import axios from '../config/axiosConfig';
import { MessageToaster } from '../utils/toastUtil';
import SmallLoader from '../components/SmallLoader';

const CWOAddTicket = () => {

    const [loading, setLoading] = useState(false);
    const navigate = useNavigate()

    // Access the context
    const { updateData } = useOutletContext<AppContextType>();
    useEffect(() => {
        handleUpdate()
        getCompanyLocations()
    }, [])

    // Update the data
    const handleUpdate = () => {
        updateData({ heading: "Add Ticket", url: pageURLs.customer_support_desk, showBackButton: true, showMainMenu: true, activeLink: "" });
    };

    const toaster = new MessageToaster();

    const [options, setOptions] = useState<Option[]>([]);

    //State to handle form data
    const [formData, setFormData] = useState<COTicketData>({
        washlocation: null,
        subject: '',
        message: '',
    });

    const formRef = useRef<HTMLFormElement>(null);

    // State to handle errors
    const [errors, setErrors] = useState<any>({});

    const getCompanyLocations = async () => {
        try {

            const { data } = await axios.get(LocationApi.CARWASH_OWNER_GET_COMPANY_LOCATIONS)
            if (data.error === 0) {
                const location_list: LocationItem[] = data?.data

                // Transform the array
                const transformedArray: Option[] = location_list.map(item => ({
                    label: item.locationName,
                    value: item.locationId,
                }));

                setOptions(transformedArray)
            }
            else {
                // show error message
                toaster.toastError(data.message);
            }
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    // Triggers whenever options is selected
    const handleSelectChange = (selectedOption: any, name: string) => {
        const value = selectedOption?.value || '';

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        const newFormData = {
            ...formData,
            [name]: value,
        };
        setFormData(newFormData);
    };

    // On input change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        const newFormData = {
            ...formData,
            [name]: value,
        };

        setFormData(newFormData);
    };

    // Triggers on submit
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        try {

            // Validate form data
            await cwoaddticketSchema.validate(formData, { abortEarly: false });
            const newformData = {

                locationId: formData.washlocation,
                subject: formData.subject,
                message: formData.message

            };

            try {
                setLoading(true)

                const { data } = await axios.post(TicketApi.CARWASH_OWNER_CREATE_TICKET, newformData);
                if (data?.error === 0) {
                    navigate(`/${pageURLs.customer_support_desk}`)

                }
                else {
                    // show error message
                    toaster.toastError(data?.message);
                }
            }


            catch (error: any) {
                throw new Error(error?.response?.data?.message || error?.message);
            }
            finally {
                setLoading(false); // Hide loader
            }
        } catch (error) {
            const newErrors: any = {};

            // If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {
                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof formData] = err.message;
                });
                setErrors(newErrors);
            }

            // Focus on the first invalid field
            if (formRef.current) {
                const firstInvalidField = Object.keys(newErrors)[0];
                const invalidElement = formRef.current.querySelector<HTMLInputElement>(`[name="${firstInvalidField}"]`);
                if (invalidElement) {
                    invalidElement.focus();
                }
            }
        }
    }

    return (

        <div className='hardware-listing-container addnew-item-wrap'>
            <Container>
                <div className="hardware-list-wrap">
                    <div className="form-inner mx-auto">
                        <form onSubmit={(e) => handleSubmit(e)} ref={formRef}>
                            <Row>
                                <Col md={6}>
                                    <CWSelect
                                        id="washlocation"
                                        name="washlocation"
                                        options={options}
                                        values={options.filter(option => option.value === formData.washlocation)}
                                        onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'washlocation')}
                                        label="Select Car Wash Location"
                                        errorMessage={errors.washlocation && errors.washlocation}
                                        dropdownGap={0}
                                        dropdownPosition="auto"
                                        placeholder="Select"
                                        multi={false}
                                        isMandatory
                                        className="no-input"
                                    />
                                </Col>
                                <Col md={6}>
                                    <CWInput
                                        id="subject"
                                        name="subject"
                                        type="text"
                                        label="Subject"
                                        value={formData.subject}
                                        onChange={handleChange}
                                        errorMessage={errors.subject}
                                        maxLength={SUBJECT_MAX_LENGTH}
                                        isMandatory
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col md={12}>
                                    <CWTextarea
                                        id="message"
                                        name="message"
                                        type="text"
                                        label="Message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        errorMessage={errors.message}
                                        maxLength={MESSAGE_MAX_LENGTH}
                                        isMandatory
                                    />
                                </Col>
                            </Row>
                            <div className="btn-sec d-flex justify-content-end">
                                <Button title="Submit Ticket" variant="primary" className="primary-btn" type="submit" >Submit Ticket</Button>
                            </div>
                        </form>
                    </div>
                </div>
            </Container >
            {loading && <SmallLoader />}

        </div >
    )
}

export default CWOAddTicket;
